import { imgPreview } from '../utils';
import { canvasPreview } from '../canvasPreview';
import { useEffect, useRef, useState } from 'react';
import { useDebounceEffect } from './useDebounceEffect';
import { CampaignsGoogleConfigsCampaignTypeEnum, useUploadImages as useUploadImageMutation } from 'api';
import { getErrorsText } from 'pages/Insights/components/Tabs/components/ErrorMessage';
import { Crop, makeAspectCrop, PixelCrop } from 'react-image-crop';
import { Severity, usePushNotifications } from 'services/NotificationService';
import { useImageUploaderDialog } from '../../../hooks/useImageUploaderDialog';
import { useGoogleAssetsBuilderStore } from 'stores/GoogleAssets/GoogleAssetsBuilderStore';

export enum Aspect {
  MARKETING_IMAGE = 'marketing_image',
  SQUARE_MARKETING_IMAGE = 'square_marketing_image',
  PORTRAIT_MARKETING_IMAGE = 'portrait_marketing_image'
}

export const useImageUploader = () => {
  const { article } = useGoogleAssetsBuilderStore();

  const imgRef = useRef<HTMLImageElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const store = useGoogleAssetsBuilderStore();
  const { handleCloseImageUploaderDialog } = useImageUploaderDialog();

  const notify = usePushNotifications();
  const [uploadImage, { loading }] = useUploadImageMutation();

  const [crop, setCrop] = useState<Crop>();
  const [imgSrc, setImgSrc] = useState<string>('');
  const [aspectRatio, setAspectRatio] = useState<number>(16 / 9);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [aspect, setAspect] = useState<Aspect>(Aspect.SQUARE_MARKETING_IMAGE);

  const isDisplayCampaign =
    article?.campaign.campaignsGoogleConfig?.campaignType === CampaignsGoogleConfigsCampaignTypeEnum.display;

  const onSelectFile = (files: File[] | null, url?: string) => {
    setCrop(undefined);

    if (url) {
      setImgSrc(url);
      return;
    }

    if (files) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader?.result?.toString() || ''));
      reader.readAsDataURL(files[0]);
    }
  };

  const resetSelection = () => {
    setImgSrc('');
    setCrop(undefined);
    setCompletedCrop(undefined);
  };

  const onImageLoad = (img: HTMLImageElement) => {
    imgRef.current = img;
    const { width, height } = img;
    const newCrop = makeAspectCrop(
      { unit: '%', width: 10, height: 10, x: 25, y: 25, aspect: aspectRatio },
      width,
      height
    );
    setCrop(newCrop);
  };

  const getImg = async () => {
    return await imgPreview(imgRef.current, crop as PixelCrop);
  };

  const handleImageUpload = async (blob: Blob | undefined) => {
    try {
      if (!blob || !store.article || !store.article.articleGroup) return;

      const { data } = await uploadImage({
        variables: { articleGroupId: Number(store.article.articleGroup.id), files: [blob] }
      });

      if (data?.uploadImages?.errors?.length) {
        const errorMsg = getErrorsText(data?.uploadImages?.errors || []);
        throw new Error(errorMsg);
      }

      handleCloseImageUploaderDialog();
      window.location.reload();

      notify({ title: 'Success', message: 'Image was successfully uploaded !', severity: Severity.SUCCESS });
    } catch (e) {
      notify({ title: 'Error', message: e.message, severity: Severity.ERROR });
    }
  };

  useEffect(() => {
    setCrop(undefined);
  }, [aspectRatio]);

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        await canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  return {
    crop,
    aspect,
    imgSrc,
    getImg,
    setCrop,
    loading,
    setAspect,
    aspectRatio,
    isDisplayCampaign,
    onImageLoad,
    onSelectFile,
    setAspectRatio,
    resetSelection,
    setCompletedCrop,
    handleImageUpload
  };
};
